// components/admin/AdminSidebar.js (Updated with more menu items)
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

const AdminSidebar = ({ activeTab, setActiveTab, isOpen, toggleSidebar, user }) => {
    const menuItems = [
        {
            id: 'categories',
            label: 'Categories',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
            ),
            color: 'pink'
        },
        {
            id: 'types',
            label: 'Complaint Types',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
            ),
            color: 'indigo'
        },
        {
            id: 'attendance',
            label: 'Admin Attendance',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
            ),
            color: 'purple'
        },
        {
            id: 'users',
            label: 'User Management',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            ),
            color: 'orange'
        },
        {
            id: 'live-tracking',
            label: 'Live Tracking',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
            ),
            color: 'teal'
        }
    ];

    const getColorClasses = (color, isActive) => {
        const colors = {
            pink: {
                bg: 'bg-pink-100',
                text: 'text-pink-600',
                active: 'bg-pink-500 text-white',
                hover: 'hover:bg-pink-50',
                shadow: 'shadow-pink-500/25'
            },
            indigo: {
                bg: 'bg-indigo-100',
                text: 'text-indigo-600',
                active: 'bg-indigo-500 text-white',
                hover: 'hover:bg-indigo-50',
                shadow: 'shadow-indigo-500/25'
            },
            green: {
                bg: 'bg-green-100',
                text: 'text-green-600',
                active: 'bg-green-500 text-white',
                hover: 'hover:bg-green-50',
                shadow: 'shadow-green-500/25'
            },
            purple: {
                bg: 'bg-purple-100',
                text: 'text-purple-600',
                active: 'bg-purple-500 text-white',
                hover: 'hover:bg-purple-50',
                shadow: 'shadow-purple-500/25'
            },
            orange: {
                bg: 'bg-orange-100',
                text: 'text-orange-600',
                active: 'bg-orange-500 text-white',
                hover: 'hover:bg-orange-50',
                shadow: 'shadow-orange-500/25'
            },
            teal: {
                bg: 'bg-teal-100',
                text: 'text-teal-600',
                active: 'bg-teal-500 text-white',
                hover: 'hover:bg-teal-50',
                shadow: 'shadow-teal-500/25'
            }
        };
        return colors[color] || colors.pink;
    };

    const handleItemClick = (item) => {
        setActiveTab(item.id);
        if (window.innerWidth < 1024) {
            toggleSidebar();
        }
    };

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                    onClick={toggleSidebar}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed top-0 left-0 h-screen w-72 bg-white shadow-2xl z-50 
                    transform transition-transform duration-300 ease-in-out
                    lg:translate-x-0 lg:static lg:shadow-lg
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                `}
            >
                {/* Sidebar Header */}
                <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-gradient-to-r from-pink-500 to-purple-500 rounded-xl">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <Link to={"/"}>
                                <h2 className="text-lg font-bold text-gray-800">Admin Panel</h2>
                                <p className="text-xs text-gray-500">Manage your system</p>
                            </Link>
                        </div>
                        <button
                            onClick={toggleSidebar}
                            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Navigation Menu */}
                <nav className="p-4 space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)' }}>
                    {menuItems.map((item) => {
                        const isActive = activeTab === item.id;
                        const colors = getColorClasses(item.color, isActive);

                        return (
                            <button
                                key={item.id}
                                onClick={() => handleItemClick(item)}
                                className={`
                                    w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200
                                    ${isActive
                                        ? `${colors.active} shadow-lg ${colors.shadow}`
                                        : `text-gray-700 ${colors.hover}`
                                    }
                                    group relative
                                `}
                            >
                                <span className={`
                                    ${isActive ? 'text-white' : colors.text}
                                    group-hover:scale-110 transition-transform duration-200
                                `}>
                                    {item.icon}
                                </span>
                                <span className="font-medium flex-1 text-left">{item.label}</span>
                                {isActive && (
                                    <span className="absolute right-3 w-1.5 h-8 bg-white rounded-full opacity-50"></span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Sidebar Footer */}
                <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                            {user?.name?.charAt(0) || 'A'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{user?.name || 'Administrator'}</p>
                            <p className="text-xs text-gray-500 truncate">{user?.email || 'admin@example.com'}</p>
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default AdminSidebar;